import React from 'react';
import {OrderHistoryType, OrdersType, ResponsePrintOrderType} from '../../../types/TypesStore';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import LightGreenButton from '../../../Components/Buttons/LightGreenButton';
import GreenButton from '../../../Components/Buttons/GreenButton';
import {
	deliveryStatusTranslate,
	deliveryTypeTranslate,
	movementsStatusTranslate,
	orderStatusTranslate,
} from '../../../assets/utils/translatersFunc';
import ExcelJS from 'exceljs';
import {saveAs} from 'file-saver';
import {useLazyPrintOrderQuery} from '../../../redux/DepotSlice/depotOrdersApi';

// Объединенный тип для заказа с полями из разных API
type OrderWithAllFields = OrdersType & {
	initial_name?: string;
	contact_name?: string;
	contact_phone?: string;
	service_type?: string;
	delivery_type?: string;
	address_name?: string;
	post_id?: string;
	delivery_date?: string;
};

interface Props {
	closePopup: () => void;
	removeButton: (id: number) => void;
	editButton: (id: number) => void;
	order?: OrderWithAllFields;
	orderHistory?: OrderHistoryType[];
	hidePrintButtons?: boolean;
}
const PopupStatus = ({
	order,
	orderHistory,
	closePopup,
	removeButton,
	editButton,
	hidePrintButtons = false,
}: Props) => {
	const {t} = useTranslation();
	const navigate = useNavigate();
	const [triggerPrintOrder, {data: printData, isLoading: isPrintLoading}] = useLazyPrintOrderQuery();

	const handlePrintPDF = () => {
		if (order?.id) {
			navigate(`/print_invoice/${order.id}/pdf`);
		}
	};

	const handlePrintExcel = async () => {
		if (!order?.id) return;

		try {
			// Получаем полные данные заказа через API печати
			const result = await triggerPrintOrder(order.id).unwrap();
			
			if (!result?.order || result.order.length === 0) {
				return;
			}

			const orderData = result.order[0]; // Берем первый заказ из массива

			const workbook = new ExcelJS.Workbook();
			const sheet = workbook.addWorksheet('Накладна');

			// Заголовок накладной
			sheet.getCell('A1').value = `Накладна №${orderData.id} від ${orderData.order_date}`;
			sheet.getCell('A1').font = {bold: true, size: 14};
			sheet.getCell('A1').alignment = {horizontal: 'left'};

			// Пустые строки для форматирования
			sheet.addRow([]);
			sheet.addRow([]);
			sheet.addRow([]);
			sheet.addRow([]);

			// Склад
			sheet.getCell('A5').value = 'Склад';
			sheet.getCell('A5').font = {bold: true};
			sheet.getCell('B5').value = orderData.ware_name;

			// Комментарий
			sheet.getCell('A6').value = 'Коментар';
			sheet.getCell('A6').font = {bold: true};
			sheet.getCell('B6').value = ''; // Комментарий не доступен в API печати

			// Пустая строка
			sheet.addRow([]);

			// Заголовки таблицы товаров
			const headerRow = 8;
			sheet.getCell(`A${headerRow}`).value = '№';
			sheet.getCell(`B${headerRow}`).value = 'Назва';
			sheet.getCell(`C${headerRow}`).value = 'Кількість';
			sheet.getCell(`D${headerRow}`).value = 'Бали';
			sheet.getCell(`E${headerRow}`).value = 'Ціна';
			sheet.getCell(`F${headerRow}`).value = 'Сума';

			// Стилизация заголовков таблицы
			for (let col = 1; col <= 6; col++) {
				const cell = sheet.getCell(headerRow, col);
				cell.font = {bold: true};
				cell.fill = {
					type: 'pattern',
					pattern: 'solid',
					fgColor: {argb: 'FFE0E0E0'},
				};
				cell.border = {
					top: {style: 'thin'},
					left: {style: 'thin'},
					bottom: {style: 'thin'},
					right: {style: 'thin'},
				};
			}

			// Добавление товаров
			if (orderData.products && orderData.products.length > 0) {
				orderData.products.forEach((product, index) => {
					const row = headerRow + 1 + index;
					sheet.getCell(`A${row}`).value = index + 1;
					sheet.getCell(`B${row}`).value = product.name || 'Назва не вказана';
					sheet.getCell(`C${row}`).value = product.count || 0;
					sheet.getCell(`D${row}`).value = product.points || 0;
					sheet.getCell(`E${row}`).value = product.money || 0;
					sheet.getCell(`F${row}`).value = (product.count || 0) * (product.money || 0);

					// Стилизация ячеек товаров
					for (let col = 1; col <= 6; col++) {
						const cell = sheet.getCell(row, col);
						cell.border = {
							top: {style: 'thin'},
							left: {style: 'thin'},
							bottom: {style: 'thin'},
							right: {style: 'thin'},
						};
					}
				});
			} else {
				// Если нет товаров, добавляем строку "Нет данных"
				const row = headerRow + 1;
				sheet.getCell(`A${row}`).value = 'Немає товарів';
				sheet.getCell(`B${row}`).value = '';
				sheet.getCell(`C${row}`).value = '';
				sheet.getCell(`D${row}`).value = '';
				sheet.getCell(`E${row}`).value = '';
				sheet.getCell(`F${row}`).value = '';
			}

			// Итоги
			const totalsStartRow = headerRow + 1 + (orderData.products?.length || 0) + 1;
			
			// Бали
			sheet.getCell(`A${totalsStartRow}`).value = 'Бали';
			sheet.getCell(`A${totalsStartRow}`).font = {bold: true};
			sheet.getCell(`B${totalsStartRow}`).value = orderData.sum_points || 0;

			// Разом
			sheet.getCell(`A${totalsStartRow + 1}`).value = 'Разом';
			sheet.getCell(`A${totalsStartRow + 1}`).font = {bold: true};
			sheet.getCell(`B${totalsStartRow + 1}`).value = orderData.sum_money || 0;

			// Настройка ширины колонок
			sheet.getColumn('A').width = 8;
			sheet.getColumn('B').width = 40;
			sheet.getColumn('C').width = 12;
			sheet.getColumn('D').width = 12;
			sheet.getColumn('E').width = 12;
			sheet.getColumn('F').width = 12;

			// Форматирование чисел
			sheet.getColumn('D').numFmt = '0.0';
			sheet.getColumn('E').numFmt = '#,##0.00';
			sheet.getColumn('F').numFmt = '#,##0.00';

			// Генерация и скачивание файла
			const buf = await workbook.xlsx.writeBuffer();
			const blob = new Blob([buf], {type: 'application/octet-stream'});
			saveAs(blob, `invoice_${orderData.id}_${new Date().toISOString().split('T')[0]}.xlsx`);
		} catch (error) {
			// Тихо обрабатываем ошибку
		}
	};

	const canEditOrDelete =
		order?.status && ['created', 'confirmed'].includes(order.status);

	return (
		<div
			id="popup-1"
			className="popup popup-1 open"
			onClick={(e) => e.stopPropagation()}
		>
			<div className="popup__body" onClick={(e) => e.stopPropagation()}>
				<div className="popup__content" onClick={(e) => e.stopPropagation()}>
					<button
						type={'button'}
						onClick={closePopup}
						className="popup__close close-popup"
					>
						x
					</button>
					{orderHistory && (
						<div className="popup__item item-top-popup">
							<div className="item-top-popup__title">
								{t('Статус вашого замовлення')}
							</div>
							<ul className="item-top-popup__row">
								{orderHistory?.map((item, index) => (
									<li
										key={item.order_id + index}
										className="item-top-popup__column"
									>
										<div className="item-top-popup__unit unit-popup">
											<div className="unit-popup__row-top">
												<div className="unit-popup__number">{index + 1}</div>
												<div className="unit-popup__line" />
											</div>
											<div className="unit-popup__create">
												{item.action_text}
											</div>
											<div className="unit-popup__date">{item.action_date}</div>
										</div>
									</li>
								))}
							</ul>
						</div>
					)}
					{order && (
						<div className="popup__item item-top-popup item-top-popup-bottom">
							<div className="item-top-popup__title">
								{t('Інформація про замовлення')}
							</div>
							<div className="item-top-popup__id">ID {order.id}</div>
							<div className="item-top-popup-bottom__row">
								<div className="item-top-popup-bottom__column">
									<div className="item-top-popup-bottom__item item-bottom-popup">
										<div className="item-bottom-popup__unit">
											{t('Дата проведення')}: <span>{order.order_date}</span>
										</div>
										<div className="item-bottom-popup__unit">
											{t('Дата створення')}: <span>{order.create_date}</span>
										</div>
										<div className="item-bottom-popup__unit">
											{t('Вид накладної')}:{' '}
											<span>{movementsStatusTranslate(order.type)}</span>
										</div>
										<div className="item-bottom-popup__unit">
											{t('Замовник')}: <span>{order.initial_name}</span>
										</div>
										<div className="item-bottom-popup__unit">
											{t('Склад обслуговування')}:{' '}
											<span>{order.ware_name}</span>
										</div>
										<div className="item-bottom-popup__unit">
											{t('Контакти отримувача')}:{' '}
											<span>
												{order.contact_name} {order.contact_phone}
											</span>
										</div>
										<div className="item-bottom-popup__unit">
											{t('Статус доставки')}:{' '}
											<span>{orderStatusTranslate(order.status)}</span>
										</div>
										<div className="item-bottom-popup__unit">
											{t('Спосіб доставки')}:{' '}
											<span>
												{order.service_type
													? deliveryStatusTranslate(order.service_type)
													: ''}
											</span>
										</div>
									</div>
								</div>
								<div className="item-top-popup-bottom__column">
									<div className="item-top-popup-bottom__item item-bottom-popup">
										<div className="item-bottom-popup__unit">
											{t('Відділення/Адреса')}:{' '}
											<span>
												{order.delivery_type
													? deliveryTypeTranslate(order.delivery_type)
													: ''}
											</span>
										</div>
										<div className="item-bottom-popup__unit">
											{t('Адреса доставки')}: <span>{order.address_name}</span>
										</div>
										<div className="item-bottom-popup__unit">
											ТТН: <span>{order.post_id}</span>
										</div>
										<div className="item-bottom-popup__unit">
											{t('Час доставки')}: <span>{order.delivery_date}</span>
										</div>
										<div className="item-bottom-popup__unit">
											{t('Коментар')}: <span>{order.comment}</span>
										</div>
									</div>
								</div>
							</div>
							<div className="item-top-popup__buttons">
								{canEditOrDelete && (
									<>
										<GreenButton
											text={t('Видалити')}
											onClick={() => removeButton(order.id)}
											type="button"
										/>
										<LightGreenButton
											text={t('редагувати')}
											onClick={() => order.id && editButton(order.id)}
											type="button"
										/>
									</>
								)}
								{!hidePrintButtons && (
									<>
										<GreenButton
											text="Друк PDF"
											onClick={handlePrintPDF}
											type="button"
										/>
										<LightGreenButton
											text="Друк Excel"
											onClick={handlePrintExcel}
											type="button"
										/>
									</>
								)}
							</div>
						</div>
					)}
					{orderHistory && orderHistory.length > 0 && (
						<div className="popup__item item-top-popup">
							<div className="item-top-popup__title">{t('Історія змін')}</div>
							<ul className="item-top-popup__row">
								{orderHistory.map((item, index) => (
									<li
										key={item.order_id + index}
										className="item-top-popup__column"
									>
										<div className="item-top-popup__unit unit-popup">
											<div className="unit-popup__row-top">
												<div className="unit-popup__number">{index + 1}</div>
												<div className="unit-popup__line" />
											</div>
											<div className="unit-popup__create">
												{item.action_text}
											</div>
											<div className="unit-popup__date">{item.action_date}</div>
										</div>
									</li>
								))}
							</ul>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default PopupStatus;